import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, User, Building, Phone, Mail, Calendar, 
  Filter, X, ChevronRight, Globe, Linkedin, Facebook, Instagram,
  MapPin, MessageCircle, Send, ArrowRight
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Métiers du bâtiment
const sectorOptions = [
  { value: 'maconnerie', label: 'Maçonnerie' },
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'electricite', label: 'Électricité' },
  { value: 'carrelage', label: 'Carrelage' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'menuiserie', label: 'Menuiserie' },
  { value: 'charpente_couverture', label: 'Charpente/Couverture' },
  { value: 'platrerie', label: 'Plâtrerie' },
  { value: 'chauffage_clim', label: 'Chauffage/Climatisation' },
  { value: 'terrassement', label: 'Terrassement' },
  { value: 'isolation', label: 'Isolation' },
  { value: 'pisciniste', label: 'Pisciniste' },
  { value: 'jardinier', label: 'Jardinier' },
  { value: 'paysagiste', label: 'Paysagiste' },
  { value: 'serrurerie', label: 'Serrurerie' },
  { value: 'facade', label: 'Façade' },
  { value: 'multi_corps', label: 'Multi-corps d\'état' },
  { value: 'autre', label: 'Autre' }
];

const sourceLabels = {
  diagnostic: { label: 'Diagnostic', color: 'bg-purple-100 text-purple-600' },
  contact: { label: 'Formulaire', color: 'bg-blue-100 text-blue-600' },
  phone: { label: 'Téléphone', color: 'bg-green-100 text-green-600' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-100 text-sky-600' },
  referral: { label: 'Recommandation', color: 'bg-amber-100 text-amber-600' },
  website: { label: 'Site web', color: 'bg-slate-100 text-slate-600' },
  other: { label: 'Autre', color: 'bg-gray-100 text-gray-600' }
};

const statusLabels = {
  new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-600' },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-600' },
  quote_todo: { label: 'Devis à faire', color: 'bg-amber-100 text-amber-600' },
  quote_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-600' },
  waiting: { label: 'En attente', color: 'bg-pink-100 text-pink-600' },
  won: { label: 'Gagné', color: 'bg-green-100 text-green-600' },
  lost: { label: 'Perdu', color: 'bg-red-100 text-red-600' }
};

export default function AdminProspectsPage() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const [newProspect, setNewProspect] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    city: '',
    postal_code: '',
    company_name: '',
    company_size: '',
    sector: '',
    website: '',
    linkedin: '',
    facebook: '',
    instagram: '',
    source: 'contact',
    interested_offer: ''
  });

  useEffect(() => {
    fetchProspects();
  }, [filterStatus, filterSource, filterSector]);

  const fetchProspects = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      let url = `${API_URL}/api/admin-crm/prospects`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSource) params.append('source', filterSource);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter by sector if needed (client-side for now)
        if (filterSector) {
          setProspects(data.filter(p => p.sector === filterSector));
        } else {
          setProspects(data);
        }
      }
    } catch (error) {
      console.error('Error fetching prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProspect = async (e) => {
    e.preventDefault();
    if (!newProspect.name || !newProspect.email) {
      toast.error('Nom et email requis');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/prospects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProspect)
      });

      if (response.ok) {
        toast.success('Prospect créé');
        setShowCreateModal(false);
        resetForm();
        fetchProspects();
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const resetForm = () => {
    setNewProspect({
      name: '',
      email: '',
      phone: '',
      whatsapp: '',
      address: '',
      city: '',
      postal_code: '',
      company_name: '',
      company_size: '',
      sector: '',
      website: '',
      linkedin: '',
      facebook: '',
      instagram: '',
      source: 'contact',
      interested_offer: ''
    });
  };

  const openWhatsApp = (phone, name) => {
    const cleanPhone = (phone || '').replace(/\s/g, '').replace(/^0/, '+33');
    const message = encodeURIComponent(`Bonjour ${name}, je te contacte de BatiAssist...`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getSectorLabel = (value) => {
    const sector = sectorOptions.find(s => s.value === value);
    return sector?.label || value || '-';
  };

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-prospects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prospects</h1>
          <p className="text-gray-500">{prospects.length} prospect{prospects.length > 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/crm/pipeline">
            <Button variant="outline">
              <ArrowRight className="w-4 h-4 mr-2" />
              Voir Pipeline
            </Button>
          </Link>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-prospect-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau prospect
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(statusLabels).map(([value, { label }]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filterSector}
          onChange={(e) => setFilterSector(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg"
        >
          <option value="">Tous les secteurs</option>
          {sectorOptions.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg"
        >
          <option value="">Toutes les sources</option>
          {Object.entries(sourceLabels).map(([value, { label }]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Prospects List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredProspects.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun prospect trouvé</p>
            <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              Créer un prospect
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredProspects.map((prospect) => {
              const source = sourceLabels[prospect.source] || sourceLabels.other;
              const status = statusLabels[prospect.status] || statusLabels.new;

              return (
                <div
                  key={prospect.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`prospect-row-${prospect.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      onClick={() => navigate(`/admin/crm/prospects/${prospect.id}`)}
                      className="flex-1 flex items-center gap-4 cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold text-lg">
                          {prospect.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{prospect.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                            {status.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${source.color}`}>
                            {source.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          {prospect.company_name && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {prospect.company_name}
                            </span>
                          )}
                          {prospect.sector && (
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {getSectorLabel(prospect.sector)}
                            </span>
                          )}
                          {prospect.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {prospect.city}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {prospect.email}
                          </span>
                          {prospect.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {prospect.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Social Links */}
                      {prospect.linkedin && (
                        <a href={prospect.linkedin} target="_blank" rel="noopener noreferrer" 
                           className="p-2 hover:bg-blue-50 rounded-lg text-blue-600" title="LinkedIn">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {prospect.facebook && (
                        <a href={prospect.facebook} target="_blank" rel="noopener noreferrer"
                           className="p-2 hover:bg-blue-50 rounded-lg text-blue-700" title="Facebook">
                          <Facebook className="w-4 h-4" />
                        </a>
                      )}
                      {prospect.instagram && (
                        <a href={prospect.instagram} target="_blank" rel="noopener noreferrer"
                           className="p-2 hover:bg-pink-50 rounded-lg text-pink-600" title="Instagram">
                          <Instagram className="w-4 h-4" />
                        </a>
                      )}
                      {prospect.website && (
                        <a href={prospect.website} target="_blank" rel="noopener noreferrer"
                           className="p-2 hover:bg-gray-100 rounded-lg text-gray-600" title="Site web">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                      {/* WhatsApp */}
                      {(prospect.whatsapp || prospect.phone) && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); openWhatsApp(prospect.whatsapp || prospect.phone, prospect.name); }}
                          className="p-2 hover:bg-green-50 rounded-lg text-green-600" 
                          title="WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="create-prospect-modal"
            >
              <div className="p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Nouveau prospect</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateProspect} className="p-6 space-y-6">
                {/* Contact Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Informations de contact
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                      <input
                        type="text"
                        required
                        value={newProspect.name}
                        onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        data-testid="prospect-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={newProspect.email}
                        onChange={(e) => setNewProspect({ ...newProspect, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        data-testid="prospect-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={newProspect.phone}
                        onChange={(e) => setNewProspect({ ...newProspect, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                      <input
                        type="tel"
                        value={newProspect.whatsapp}
                        onChange={(e) => setNewProspect({ ...newProspect, whatsapp: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adresse
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input
                        type="text"
                        value={newProspect.address}
                        onChange={(e) => setNewProspect({ ...newProspect, address: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="123 Rue de la Paix"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                      <input
                        type="text"
                        value={newProspect.postal_code}
                        onChange={(e) => setNewProspect({ ...newProspect, postal_code: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="75001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                      <input
                        type="text"
                        value={newProspect.city}
                        onChange={(e) => setNewProspect({ ...newProspect, city: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="Paris"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Entreprise
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise</label>
                      <input
                        type="text"
                        value={newProspect.company_name}
                        onChange={(e) => setNewProspect({ ...newProspect, company_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Taille</label>
                      <select
                        value={newProspect.company_size}
                        onChange={(e) => setNewProspect({ ...newProspect, company_size: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      >
                        <option value="">Sélectionner</option>
                        <option value="1-5">1-5 employés</option>
                        <option value="6-10">6-10 employés</option>
                        <option value="11-20">11-20 employés</option>
                        <option value="20+">20+ employés</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Secteur d'activité (Métier)</label>
                      <select
                        value={newProspect.sector}
                        onChange={(e) => setNewProspect({ ...newProspect, sector: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        data-testid="prospect-sector"
                      >
                        <option value="">Sélectionner un métier</option>
                        {sectorOptions.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Liens web & réseaux sociaux
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Site internet</label>
                      <input
                        type="url"
                        value={newProspect.website}
                        onChange={(e) => setNewProspect({ ...newProspect, website: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="https://www.entreprise.fr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                      <input
                        type="url"
                        value={newProspect.linkedin}
                        onChange={(e) => setNewProspect({ ...newProspect, linkedin: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="https://linkedin.com/in/..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                      <input
                        type="url"
                        value={newProspect.facebook}
                        onChange={(e) => setNewProspect({ ...newProspect, facebook: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                      <input
                        type="url"
                        value={newProspect.instagram}
                        onChange={(e) => setNewProspect({ ...newProspect, instagram: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                  </div>
                </div>

                {/* Source & Interest */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Source & Intérêt
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                      <select
                        value={newProspect.source}
                        onChange={(e) => setNewProspect({ ...newProspect, source: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      >
                        {Object.entries(sourceLabels).map(([value, { label }]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Forfait intéressé</label>
                      <select
                        value={newProspect.interested_offer}
                        onChange={(e) => setNewProspect({ ...newProspect, interested_offer: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                      >
                        <option value="">Non défini</option>
                        <option value="essentiel">Essentiel (499€)</option>
                        <option value="business">Business (949€)</option>
                        <option value="premium">Premium (1499€)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-blue-600" data-testid="submit-prospect-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le prospect
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
